import { useState, useCallback, useMemo, useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { useMap } from "@vis.gl/react-google-maps";
import { List, MapIcon } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { OwnershipBadge } from "@/components/locations/OwnershipBadge";
import { ClassificationBadge } from "@/components/locations/ClassificationBadge";
import { PublicMapTooltip } from "@/components/share/PublicMapTooltip";
import { PublicLocationSidebar } from "@/components/share/PublicLocationSidebar";
import { PublicExportButton } from "@/components/share/PublicExportButton";
import { PublicFilters } from "@/components/share/PublicFilters";
import { useClusteredMarkers } from "@/hooks/useClusteredMarkers";
import { useMapTooltip } from "@/hooks/useMapTooltip";
import { cn } from "@/lib/utils";
import type { OwnershipType } from "@/types/domain";
import { resolveHasSportField } from "@/lib/classification";

function getConfidence(
  classifications: Array<{ confidence: number | null; source: string }> | null,
): number | null {
  if (!classifications || classifications.length === 0) return null;
  const satellite = classifications.find((c) => c.source === "satellite");
  return satellite?.confidence ?? classifications[0]?.confidence ?? null;
}

interface SharedMapContentProps {
  token: string;
  mapData: any[];
  onHoveredLocationChange: (id: string | null) => void;
  onSelectedLocationChange: (id: string | null) => void;
  selectedLocationId: string | null;
}

function SharedMapContent({
  token,
  mapData,
  onHoveredLocationChange,
  onSelectedLocationChange,
  selectedLocationId,
}: SharedMapContentProps) {
  const map = useMap();
  const { setMarkerRef, getMarker } = useClusteredMarkers(false);
  const { tooltipLocationId, anchorElement, onMarkerMouseEnter, onMarkerMouseLeave, closeTooltip } =
    useMapTooltip({ getMarker });

  useEffect(() => {
    onHoveredLocationChange(tooltipLocationId);
  }, [tooltipLocationId, onHoveredLocationChange]);

  useEffect(() => {
    if (!map) return;
    const clickListener = map.addListener("click", () => closeTooltip());
    const dragListener = map.addListener("dragstart", closeTooltip);
    return () => {
      clickListener.remove();
      dragListener.remove();
    };
  }, [map, closeTooltip]);

  const markerDataMap = useMemo(() => {
    const m = new Map<string, {
      id: string;
      name: string;
      sportTypes: string[] | null;
      ownership: string;
      confidence: number | null;
      address: string | null;
    }>();
    for (const loc of mapData) {
      m.set(loc.id, {
        id: loc.id,
        name: loc.name,
        sportTypes: null,
        ownership: loc.ownership,
        confidence: getConfidence(loc.classifications),
        address: loc.address ?? null,
      });
    }
    return m;
  }, [mapData]);

  const handleMarkerClick = useCallback(
    (id: string) => onSelectedLocationChange(id),
    [onSelectedLocationChange],
  );

  const handleViewDetails = useCallback(
    (id: string) => {
      closeTooltip();
      onSelectedLocationChange(id);
    },
    [closeTooltip, onSelectedLocationChange],
  );

  const handleZoomToLocation = useCallback(
    (locationId: string) => {
      const loc = mapData.find((l: any) => l.id === locationId);
      if (map && loc) {
        map.panTo({ lat: loc.lat, lng: loc.lng });
        map.setZoom(18);
        map.setMapTypeId("hybrid");
        const sidebarEl = document.querySelector<HTMLElement>("[data-location-sidebar]");
        const offset = sidebarEl
          ? sidebarEl.offsetWidth / 2
          : window.innerWidth >= 1536 ? 280
          : window.innerWidth >= 1280 ? 240
          : 220;
        map.panBy(offset, 0);
      }
    },
    [map, mapData],
  );

  return (
    <>
      {mapData.map((loc: any) => {
        const hasSportField = resolveHasSportField(loc.classifications);
        return (
          <MapMarker
            key={loc.id}
            locationId={loc.id}
            position={{ lat: loc.lat, lng: loc.lng }}
            hasSportField={hasSportField}
            primaryType={loc.primary_type}
            name={loc.name}
            ownership={loc.ownership}
            isReviewed={loc.is_reviewed ?? false}
            isRejected={loc.is_rejected ?? false}
            isSelected={loc.id === selectedLocationId}
            onClick={handleMarkerClick}
            onMouseEnter={onMarkerMouseEnter}
            onMouseLeave={onMarkerMouseLeave}
            setMarkerRef={setMarkerRef}
          />
        );
      })}

      {tooltipLocationId && anchorElement && markerDataMap.has(tooltipLocationId) && (
        <PublicMapTooltip
          token={token}
          locationId={tooltipLocationId}
          anchor={anchorElement}
          markerData={markerDataMap.get(tooltipLocationId)!}
          onClose={closeTooltip}
          onViewDetails={handleViewDetails}
        />
      )}

      <PublicLocationSidebar
        token={token}
        locationId={selectedLocationId}
        onClose={() => onSelectedLocationChange(null)}
        onZoomToLocation={handleZoomToLocation}
      />
    </>
  );
}

export function SharedCollectionPage() {
  const params = useParams({ strict: false });
  const token = (params as { token?: string }).token as string;

  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [sportTypeFilter, setSportTypeFilter] = useState("all");
  const [ownershipFilter, setOwnershipFilter] = useState("all");

  const {
    data: collection,
    isLoading: colLoading,
    error: colError,
  } = trpc.share.getCollection.useQuery(
    { token },
    { enabled: !!token },
  );

  const { data: mapData } = trpc.share.getMapData.useQuery(
    { token },
    { enabled: !!token },
  );

  const { data: locationsData, isLoading: locsLoading } =
    trpc.share.listLocations.useQuery(
      { token, limit: 500 },
      { enabled: !!token },
    );

  // Derive available sport types from map data (from classifications or primary_type)
  const availableSportTypes = useMemo(() => {
    if (!mapData) return [];
    const types = new Set<string>();
    for (const loc of mapData) {
      if (loc.primary_type) types.add(loc.primary_type);
    }
    return [...types].sort();
  }, [mapData]);

  // Client-side filtering
  const filteredMapData = useMemo(() => {
    if (!mapData) return [];
    return mapData.filter((loc: any) => {
      if (ownershipFilter !== "all" && loc.ownership !== ownershipFilter) return false;
      if (sportTypeFilter !== "all" && loc.primary_type !== sportTypeFilter) return false;
      return true;
    });
  }, [mapData, sportTypeFilter, ownershipFilter]);

  const filteredLocations = useMemo(() => {
    const locs = locationsData?.locations ?? [];
    return locs.filter((loc: any) => {
      if (ownershipFilter !== "all" && loc.ownership !== ownershipFilter) return false;
      if (sportTypeFilter !== "all" && loc.primary_type !== sportTypeFilter) return false;
      return true;
    });
  }, [locationsData, sportTypeFilter, ownershipFilter]);

  const handleRowClick = useCallback(
    (locationId: string) => setSelectedLocationId(locationId),
    [],
  );

  const handleHoveredLocationChange = useCallback(
    (id: string | null) => setHoveredLocationId(id),
    [],
  );

  const handleSelectedLocationChange = useCallback(
    (id: string | null) => setSelectedLocationId(id),
    [],
  );

  const handleResetFilters = useCallback(() => {
    setSportTypeFilter("all");
    setOwnershipFilter("all");
  }, []);

  const mapCenter =
    filteredMapData.length > 0
      ? {
          lat: filteredMapData.reduce((sum: number, m: any) => sum + m.lat, 0) / filteredMapData.length,
          lng: filteredMapData.reduce((sum: number, m: any) => sum + m.lng, 0) / filteredMapData.length,
        }
      : undefined;

  if (colLoading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-[70%] bg-muted animate-pulse" />
          <div className="w-[30%] p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (colError || !collection) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-screen">
        <MapIcon className="size-12 text-muted-foreground" />
        <p className="text-destructive text-lg font-medium">
          Collection not found
        </p>
        <p className="text-sm text-muted-foreground">
          This share link may be invalid or the collection may have been deleted.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b shrink-0">
        <div className="flex items-center gap-2 mr-auto">
          <span
            className="h-3.5 w-3.5 rounded-full"
            style={{ backgroundColor: collection.color }}
          />
          <h1 className="text-lg font-semibold">{collection.name}</h1>
          <span className="text-sm text-muted-foreground">
            ({collection.locationCount})
          </span>
        </div>
        {collection.description && (
          <span className="text-sm text-muted-foreground max-w-md truncate hidden md:block">
            {collection.description}
          </span>
        )}
        <PublicExportButton token={token} />
      </div>

      {/* Split view: map + list */}
      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className="w-[70%] border-r relative">
          <GoogleMapWrapper
            defaultCenter={mapCenter}
            defaultZoom={filteredMapData.length > 0 ? 13 : 7}
            className="h-full w-full"
          >
            <SharedMapContent
              token={token}
              mapData={filteredMapData}
              onHoveredLocationChange={handleHoveredLocationChange}
              onSelectedLocationChange={handleSelectedLocationChange}
              selectedLocationId={selectedLocationId}
            />
          </GoogleMapWrapper>
        </div>

        {/* Right panel */}
        <div className="w-[30%] flex flex-col overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b shrink-0">
            <List className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium">Locations</span>
            <span className="text-xs text-muted-foreground">({filteredLocations.length})</span>
          </div>

          <PublicFilters
            sportTypes={availableSportTypes}
            selectedSportType={sportTypeFilter}
            onSportTypeChange={setSportTypeFilter}
            selectedOwnership={ownershipFilter}
            onOwnershipChange={setOwnershipFilter}
            onReset={handleResetFilters}
          />

          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Ownership</TableHead>
                  <TableHead>Classification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredLocations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="h-32 text-center text-muted-foreground"
                    >
                      {(sportTypeFilter !== "all" || ownershipFilter !== "all")
                        ? "No locations match the current filters"
                        : "No locations in this collection"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLocations.map((loc: any) => {
                    const classification =
                      loc.classifications?.find(
                        (c: any) => c.source === "satellite",
                      ) ?? loc.classifications?.[0];

                    const isHovered = hoveredLocationId === loc.id;

                    return (
                      <TableRow
                        key={loc.id}
                        className={cn(
                          "cursor-pointer transition-colors",
                          isHovered && "bg-primary/10 ring-1 ring-inset ring-primary/20",
                        )}
                        onClick={() => handleRowClick(loc.id)}
                      >
                        <TableCell>
                          <span className="font-medium max-w-[180px] truncate block">
                            {loc.name}
                          </span>
                          <span className="text-xs text-muted-foreground truncate block max-w-[180px]">
                            {loc.address ?? "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <OwnershipBadge
                            ownership={loc.ownership as OwnershipType}
                          />
                        </TableCell>
                        <TableCell>
                          <ClassificationBadge
                            hasSportField={
                              classification?.has_sport_field ?? null
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
